// Candidatures d'une campagne : liste chronologique filtrable, envoi groupé, comparaison côte à côte sans classement.
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { post } from "@core/api";
import { date, dateTime, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Button, Card, Checkbox, Empty, ErrorBox, Input, Loading, Modal, Notice, Select, StatusChip, Table, Textarea, fieldError, useToast, type CheckStatus } from "@core/ui";
import type { Campaign } from "./campaigns";
import type { TemplatesData } from "./campaignTabs";
import { APP_STATUS_LABELS, AppStatusBadge, CheckCounts, parisDateTime } from "./shared";

type Row = {
  id: string;
  status: string;
  status_label: string;
  household_label: string;
  source: string;
  created_at: string;
  submitted_at: string | null;
  household_visible: boolean;
  tenants: number | null;
  guarantors: number | null;
  guarantee_kinds: string[];
  tenants_declared_income_cents: number | null;
  follow: { stage: string; stage_label: string; assignee_name: string | null; next_action: string; next_action_on: string | null };
  next_visit_at: string | null;
  last_visit_attendance: string | null;
  unread_messages: number;
  checks: { total: number; by_status: Record<string, number> };
};
type ListData = { items: Row[]; counts: Record<string, number>; stages: { value: string; label: string }[] };

const GUARANTEE: Record<string, string> = { personne: "garant", visale: "Visale", organisme: "organisme" };

export function ApplicationsTab({ campaign: c, onCompare }: { campaign: Campaign; onCompare: (ids: string[]) => void }) {
  const org = useOrg();
  const [status, setStatus] = useState("");
  const [stage, setStage] = useState("");
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState<"recent" | "ancien">("recent");
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (stage) params.set("stage", stage);
  if (search.trim()) params.set("q", search.trim());
  params.set("ordre", order);
  const q = useGet<ListData>(org.api(`/campaigns/${c.id}/applications?${params.toString()}`));
  const rows = q.data?.items ?? [];
  const selectable = (r: Row) => r.household_visible;
  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const statusOptions = [
    { value: "", label: "Tous les statuts" },
    ...Object.entries(APP_STATUS_LABELS).map(([v, l]) => ({ value: v, label: `${l}${q.data?.counts[v] ? ` (${q.data.counts[v]})` : ""}` })),
  ];
  return (
    <div className="stack location-top">
      <div className="form-row">
        <Select label="Statut du dossier" value={status} options={statusOptions} onChange={(e) => setStatus(e.target.value)} />
        <Select label="Suivi" value={stage} options={[{ value: "", label: "Toutes les étapes" }, ...(q.data?.stages ?? [])]} onChange={(e) => setStage(e.target.value)} />
        <Input label="Rechercher un nom" type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Select
          label="Ordre"
          value={order}
          options={[
            { value: "recent", label: "Plus récents d’abord" },
            { value: "ancien", label: "Plus anciens d’abord" },
          ]}
          onChange={(e) => setOrder(e.target.value as "recent" | "ancien")}
        />
      </div>
      <div className="row location-selection" aria-live="polite">
        <span className="small">{selected.length ? `${selected.length} dossier(s) sélectionné(s)` : "Cochez des dossiers pour les comparer ou leur écrire."}</span>
        <Button small variant="secondaire" disabled={selected.length < 2 || selected.length > 4} onClick={() => onCompare(selected)}>
          Comparer (2 à 4)
        </Button>
        {org.can("operate") ? (
          <Button small variant="secondaire" disabled={!selected.length} onClick={() => setBulkOpen(true)}>
            Écrire aux candidats sélectionnés
          </Button>
        ) : null}
        {selected.length ? (
          <Button small variant="discret" onClick={() => setSelected([])}>
            Vider la sélection
          </Button>
        ) : null}
      </div>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && !rows.length ? (
        <Empty title={status || stage || search ? "Aucun dossier ne correspond à ces filtres." : "Aucun dossier pour cette campagne."}>
          {status || stage || search ? "Modifiez les filtres." : "Partagez le lien de candidature pour recevoir les premiers."}
        </Empty>
      ) : null}
      {rows.length ? (
        <Table caption="Candidatures de la campagne, par date">
          <thead>
            <tr>
              <th>
                <span className="sr-only">Sélection</span>
              </th>
              <th>Foyer</th>
              <th>Statut du dossier</th>
              <th>Suivi</th>
              <th>Composition</th>
              <th className="right">Revenus déclarés</th>
              <th>Constats</th>
              <th>Visite</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  {selectable(r) ? (
                    <input type="checkbox" className="location-check" aria-label={`Sélectionner ${r.household_label}`} checked={selected.includes(r.id)} onChange={() => toggle(r.id)} />
                  ) : null}
                </td>
                <td>
                  <Link to={org.to(`candidatures/${r.id}`)}>{r.household_label || "Candidat"}</Link>
                  {r.unread_messages ? <span className="block small location-unread">{r.unread_messages} message(s) non lu(s)</span> : null}
                </td>
                <td>
                  <AppStatusBadge status={r.status} label={r.status_label} />
                </td>
                <td>
                  {r.follow.stage_label}
                  {r.follow.next_action ? <span className="block muted small">{r.follow.next_action}</span> : null}
                  {r.follow.assignee_name ? <span className="block muted small">Suivi par {r.follow.assignee_name}</span> : null}
                </td>
                <td>
                  {r.household_visible ? (
                    <>
                      {r.tenants} locataire{(r.tenants ?? 0) > 1 ? "s" : ""}
                      {r.guarantors ? (
                        <span className="block muted small">
                          {r.guarantors} garantie{r.guarantors > 1 ? "s" : ""} ({r.guarantee_kinds.map((k) => GUARANTEE[k] ?? k).join(", ")})
                        </span>
                      ) : (
                        <span className="block muted small">Sans garant déclaré</span>
                      )}
                    </>
                  ) : (
                    <span className="muted small">Non transmis</span>
                  )}
                </td>
                <td className="right num">{r.tenants_declared_income_cents !== null ? money(r.tenants_declared_income_cents) : "—"}</td>
                <td>{r.household_visible ? <CheckCounts counts={r.checks.by_status} /> : <span className="muted small">—</span>}</td>
                <td>
                  {r.next_visit_at ? parisDateTime(r.next_visit_at, true) : r.last_visit_attendance ? (r.last_visit_attendance === "venu" ? "Venu" : "Absent") : "—"}
                </td>
                <td className="small">{r.submitted_at ? `Déposé le ${date(r.submitted_at)}` : `Commencé le ${date(r.created_at)}`}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      <p className="small muted">
        Liste chronologique : Bailivo ne classe pas les dossiers. Le statut du dossier suit les contrôles et votre décision ; le suivi (à contacter, visite…) est votre organisation, indépendante des contrôles. Revenus déclarés par les candidats, non vérifiés.
      </p>
      <BulkMessage open={bulkOpen} onClose={() => setBulkOpen(false)} campaignId={c.id} ids={selected} rows={rows} />
    </div>
  );
}

type BulkItem = { application_id: string; household_label: string; recipients: { name: string; role_label: string }[]; body: string; unknown_variables: string[] };

function BulkMessage({ open, onClose, campaignId, ids, rows }: { open: boolean; onClose: () => void; campaignId: string; ids: string[]; rows: Row[] }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const templates = useGet<TemplatesData>(open ? org.api("/reply-templates") : null);
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState<{ items: BulkItem[]; skipped: { household_label: string; reason: string }[] } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const all = [...(templates.data?.items ?? []), ...(templates.data?.suggestions ?? [])];

  async function run(send: boolean) {
    setBusy(true);
    setError(null);
    try {
      const r = await post<{ items: BulkItem[]; skipped: any[]; sent: number }>(org.api(`/campaigns/${campaignId}/messages/bulk`), { application_ids: ids, body, preview: !send });
      if (send) {
        toast(`${r.sent} message(s) envoyé(s), un par candidature.`);
        setPreview(null);
        setBody("");
        invalidate(org.api(`/campaigns/${campaignId}/applications`), org.api("/applications"));
        onClose();
      } else {
        setPreview(r);
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Écrire aux candidats sélectionnés"
      footer={
        <>
          <Button variant="discret" onClick={onClose}>
            Annuler
          </Button>
          {preview ? (
            <Button busy={busy} onClick={() => run(true)} disabled={!preview.items.length}>
              Envoyer {preview.items.length} message(s)
            </Button>
          ) : (
            <Button busy={busy} onClick={() => run(false)} disabled={!body.trim()}>
              Prévisualiser
            </Button>
          )}
        </>
      }
    >
      <div className="stack">
        <p className="small">
          Un message individuel par candidature, sans destinataire en copie : {ids.map((id) => rows.find((r) => r.id === id)?.household_label ?? "candidature").join(", ")}.
        </p>
        <ErrorBox error={error} />
        {all.length ? (
          <Select
            label="Partir d’une réponse type"
            value=""
            options={[{ value: "", label: "Choisir…" }, ...all.map((t, i) => ({ value: String(i), label: t.title }))]}
            onChange={(e) => {
              if (e.target.value !== "") {
                setBody(all[Number(e.target.value)].body);
                setPreview(null);
              }
            }}
          />
        ) : null}
        <Textarea
          label="Message"
          rows={8}
          value={body}
          error={fieldError(error, "body")}
          onChange={(e) => {
            setBody(e.target.value);
            setPreview(null);
          }}
          help="Les variables comme {{prenom}} sont remplacées pour chaque candidature. Aucun contenu de pièce n’est joint ; le candidat lit le message dans son espace."
        />
        {preview ? (
          <div className="stack-sm">
            <p className="stat-label">Aperçu avant envoi</p>
            {preview.items.map((it) => (
              <div key={it.application_id} className="location-preview">
                <p className="small">
                  <strong>{it.household_label}</strong> · destinataires : {it.recipients.map((r) => `${r.name} (${r.role_label.toLowerCase()})`).join(", ") || "aucune adresse"}
                </p>
                {it.unknown_variables.length ? <Notice tone="attention">Variable non remplacée : {it.unknown_variables.join(", ")}</Notice> : null}
                <pre className="message-body">{it.body}</pre>
              </div>
            ))}
            {preview.skipped.map((s, i) => (
              <p key={i} className="small muted">
                Non envoyé à {s.household_label} : {s.reason}.
              </p>
            ))}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Comparaison

type ComparePerson = {
  role: string;
  role_label: string;
  display_name: string;
  situation_label: string;
  employer_name: string;
  declared_monthly_income_cents: number | null;
  guarantor_of_name: string | null;
  guarantee_kind: string;
  guarantee_kind_label: string;
  account_linked: boolean;
  confirmed_at: string | null;
};
type CompareItem = {
  application: { id: string; status: string; status_label: string; household_label: string; submitted_at: string | null };
  persons: ComparePerson[];
  declared: { tenants: number; guarantors: number; tenants_declared_income_cents: number; tenants_income_missing: number; guarantors_declared_income_cents: number; guarantors_income_missing: number };
  checks: { done: number; families: { family: string; label: string; checked: boolean; counts: Record<string, number> }[] };
  criteria: { state: string; items: { title: string; status: string; explanation: string }[] };
};

export function CompareTab({ campaign: c, ids, onSelect }: { campaign: Campaign; ids: string[]; onSelect: (ids: string[]) => void }) {
  const org = useOrg();
  const valid = ids.length >= 2 && ids.length <= 4;
  const q = useGet<{ notice: string; items: CompareItem[] }>(valid ? org.api(`/campaigns/${c.id}/comparison?ids=${ids.join(",")}`) : null);
  const list = useGet<ListData>(org.api(`/campaigns/${c.id}/applications?ordre=ancien`));
  const [picking, setPicking] = useState<string[]>(ids);
  const candidates = useMemo(() => (list.data?.items ?? []).filter((r) => r.household_visible), [list.data]);
  const families = q.data?.items[0]?.checks.families.map((f) => ({ family: f.family, label: f.label })) ?? [];

  return (
    <div className="stack location-top">
      <Notice title="Vous décidez">
        {q.data?.notice ??
          "La comparaison présente les dossiers dans l’ordre de votre sélection, sans note ni classement. Un contrôle absent n’est pas un résultat favorable."}
      </Notice>
      <Card title="Dossiers à comparer">
        {list.isLoading ? <Loading /> : null}
        {candidates.length < 2 ? <p className="muted">Il faut au moins deux dossiers déposés pour comparer.</p> : null}
        <div className="location-pick">
          {candidates.map((r) => (
            <Checkbox
              key={r.id}
              label={
                <>
                  {r.household_label} <span className="muted small">· {r.status_label}</span>
                </>
              }
              checked={picking.includes(r.id)}
              onChange={(v) => setPicking((p) => (v ? [...p, r.id] : p.filter((x) => x !== r.id)))}
            />
          ))}
        </div>
        <div className="row">
          <Button small disabled={picking.length < 2 || picking.length > 4} onClick={() => onSelect(picking)}>
            Comparer ces {picking.length} dossiers
          </Button>
          {picking.length > 4 ? <span className="small">Quatre dossiers au maximum.</span> : null}
        </div>
      </Card>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data ? (
        <div className="table-wrap">
          <table className="location-compare">
            <caption className="sr-only">Comparaison des dossiers, dans l’ordre de votre sélection</caption>
            <thead>
              <tr>
                <th scope="col">
                  <span className="sr-only">Élément comparé</span>
                </th>
                {q.data.items.map((it) => (
                  <th key={it.application.id} scope="col">
                    <Link to={org.to(`candidatures/${it.application.id}`)}>{it.application.household_label}</Link>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Statut du dossier</th>
                {q.data.items.map((it) => (
                  <td key={it.application.id}>
                    <AppStatusBadge status={it.application.status} label={it.application.status_label} />
                    <span className="block muted small">Déposé le {date(it.application.submitted_at)}</span>
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Composition du foyer et situation</th>
                {q.data.items.map((it) => (
                  <td key={it.application.id}>
                    <ul className="list-plain small">
                      {it.persons
                        .filter((p) => p.role !== "garant")
                        .map((p, i) => (
                          <li key={i}>
                            <strong>{p.display_name}</strong> ({p.role_label.toLowerCase()}) · {p.situation_label}
                            {p.employer_name ? ` · ${p.employer_name}` : ""}
                          </li>
                        ))}
                    </ul>
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Revenus mensuels déclarés (non vérifiés)</th>
                {q.data.items.map((it) => (
                  <td key={it.application.id} className="num">
                    {money(it.declared.tenants_declared_income_cents)}
                    {it.declared.tenants_income_missing ? <span className="block small">+ {it.declared.tenants_income_missing} revenu(s) non renseigné(s)</span> : null}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Garanties</th>
                {q.data.items.map((it) => {
                  const g = it.persons.filter((p) => p.role === "garant");
                  return (
                    <td key={it.application.id}>
                      {g.length ? (
                        <ul className="list-plain small">
                          {g.map((p, i) => (
                            <li key={i}>
                              {p.guarantee_kind === "personne" ? (
                                <>
                                  {p.display_name} · {p.situation_label}
                                  {p.declared_monthly_income_cents !== null ? ` · ${money(p.declared_monthly_income_cents)} déclarés` : ""}
                                  {p.guarantor_of_name ? ` · garant de ${p.guarantor_of_name}` : " · garant du foyer"}
                                  <span className="block muted">{p.confirmed_at ? "Participation confirmée par le garant" : "Non confirmé par le garant"}</span>
                                </>
                              ) : (
                                <>
                                  {p.guarantee_kind_label}
                                  {p.guarantee_kind === "visale" ? <span className="block muted">Un visa n’est pas une garantie activée.</span> : null}
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="muted small">Aucune garantie déclarée</span>
                      )}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <th scope="row">Critères que vous avez fixés</th>
                {q.data.items.map((it) => (
                  <td key={it.application.id}>
                    {it.criteria.items.length ? (
                      <ul className="list-plain small location-criteria">
                        {it.criteria.items.map((k, i) => (
                          <li key={i}>
                            <StatusChip status={k.status as CheckStatus} />
                            <span className="block">{k.title}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="muted small">Non évalués</span>
                    )}
                  </td>
                ))}
              </tr>
              {families.map((f) => (
                <tr key={f.family}>
                  <th scope="row">Constats · {f.label}</th>
                  {q.data!.items.map((it) => {
                    const fam = it.checks.families.find((x) => x.family === f.family);
                    return <td key={it.application.id}>{fam?.checked ? <CheckCounts counts={fam.counts} /> : <span className="muted small">Non contrôlé</span>}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {q.data ? <p className="small muted">Chaque constat se consulte avec sa source dans l’onglet Contrôles de la candidature. Dernière mise à jour : {dateTime(new Date().toISOString())}.</p> : null}
    </div>
  );
}
